import { useEffect, useState } from "react"
import { Link } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type {
  DealDrawer as DealDrawerData,
  DealDrawerRosterContact,
} from "@/types/v2/conversation"
import type { DealPropKey } from "@/lib/deal-prop-update"
import { DealContactPicker } from "@/components/v2/deals/deal-contact-picker"
import {
  pushDrawer,
  type DrawerNavContext,
} from "@/lib/drawer-trail"
import { useReturnTo } from "@/hooks/use-return-to"
import { useStartCall } from "@/hooks/use-start-call"
import { useDealContacts } from "@/hooks/use-deal-contacts"

// Contacts roster section rendered in the right rail of the
// desktop deal slideout (`DealDrawer`), the deal-index
// slideover, and the full deal show page (`DealShowPanel`).
//
// CON-260: replaces the single-contact card with a full
// roster — primary first, then alphabetical. Each row shows
// avatar, name + Primary chip, role (editable inline), Call
// affordance, and a kebab with Make primary / Remove.
//
// The "+ Add" button opens the manage picker which combines
// the roster and the "Add from Company" list in one panel.

interface DealContactCardProps {
  deal: DealDrawerData
  dealPropKey: DealPropKey
  // When present, the contact name renders as a button
  // that pushes the contact onto the drawer trail (deal
  // → contact navigation inside the unified slideover).
  navContext?: DrawerNavContext
  // When true, the contact name renders as an Inertia
  // `<Link>` to the contact's full-page show with the
  // current page chained as `return_to`. Used on the full
  // deal show page; ignored when `navContext` is also
  // supplied.
  linkToContactShow?: boolean
  // Picker presentation: a floating popover anchored to the
  // "+ Add" trigger on desktop/tablet, or a bottom sheet on
  // mobile (the deal show page passes "sheet"). Defaults to
  // "popover".
  pickerVariant?: "popover" | "sheet"
}

export function DealContactCard({
  deal,
  dealPropKey,
  navContext,
  linkToContactShow,
  pickerVariant = "popover",
}: DealContactCardProps) {
  const { t } = useLingui()
  const contacts = deal.contacts ?? []
  const [pickerOpen, setPickerOpen] = useState(false)
  const { setRole, makePrimary, removeContact, submitting } =
    useDealContacts({ deal, dealPropKey })

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <SectionLabel>
          <Trans>Contacts</Trans>
        </SectionLabel>
        {contacts.length > 0 ? (
          <span className="flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#7A746B] text-[10px] leading-3 font-medium px-1.5 min-w-[18px] h-[18px]">
            {contacts.length}
          </span>
        ) : null}
        <div className="grow" />
        {/* The "+ Add" button IS the picker trigger so the
            popover anchors to it (and trigger clicks count as
            inside-clicks); on mobile the same button opens the
            bottom sheet. */}
        <DealContactPicker
          deal={deal}
          dealPropKey={dealPropKey}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          variant={pickerVariant}
          triggerLabel={t`Manage contacts`}
          trigger={
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="contact-roster-add"
              className="text-[11px] leading-3.5 font-medium text-[#B86A2D] hover:underline cursor-pointer"
            >
              <Trans>+ Add</Trans>
            </button>
          }
        />
      </div>

      {contacts.length === 0 ? null : (
        <div className="flex flex-col rounded-xl bg-card border border-[#E5DFD3] divide-y divide-[#F0EDE6]">
          {contacts.map((contact) => (
            <RosterRow
              key={contact.id}
              contact={contact}
              dealId={deal.id}
              navContext={navContext}
              linkToContactShow={
                navContext ? false : Boolean(linkToContactShow)
              }
              onSetRole={(role) => setRole(contact.id, role)}
              onMakePrimary={() => makePrimary(contact.id)}
              onRemove={() => removeContact(contact.id)}
              submitting={submitting}
            />
          ))}
        </div>
      )}
    </section>
  )
}

// ── Individual roster row ───────────────────────────────

interface RosterRowProps {
  contact: DealDrawerRosterContact
  dealId: number
  navContext?: DrawerNavContext
  linkToContactShow: boolean
  onSetRole: (role: string) => void
  onMakePrimary: () => void
  onRemove: () => void
  submitting: boolean
}

function RosterRow({
  contact,
  dealId: _dealId,
  navContext,
  linkToContactShow,
  onSetRole,
  onMakePrimary,
  onRemove,
  submitting,
}: RosterRowProps) {
  const [kebabOpen, setKebabOpen] = useState(false)

  return (
    <div
      data-testid={`contact-roster-row-${contact.id}`}
      className="flex flex-col py-3 px-4 gap-2"
    >
      {/* Top row: avatar, name+chip, call button, kebab */}
      <div className="flex items-start gap-3 min-w-0">
        <span className="shrink-0 flex items-center justify-center rounded-full bg-[#E5DFD3] size-9">
          <span className="font-semibold text-foreground text-[11px] leading-3.5">
            {contact.initials}
          </span>
        </span>

        <div className="grow min-w-0 flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <ContactName
              contact={contact}
              navContext={navContext}
              linkToContactShow={linkToContactShow}
            />
            {contact.primary ? (
              <span
                data-testid={`contact-primary-chip-${contact.id}`}
                className="shrink-0 text-[9px] leading-3 font-semibold text-[#B86A2D] bg-[#FAF1E6] px-1.5 py-0.5 rounded-full"
              >
                <Trans>Primary</Trans>
              </span>
            ) : null}
          </div>

          {/* Role row — inline edit */}
          <RoleRow
            contactId={contact.id}
            role={contact.role}
            onSetRole={onSetRole}
            submitting={submitting}
          />
        </div>

        <div className="shrink-0 flex items-center gap-1.5">
          <CallButton contactId={contact.id} phone={contact.phone} />
          <KebabMenu
            contact={contact}
            open={kebabOpen}
            onOpenChange={setKebabOpen}
            onMakePrimary={() => {
              setKebabOpen(false)
              onMakePrimary()
            }}
            onRemove={() => {
              setKebabOpen(false)
              onRemove()
            }}
          />
        </div>
      </div>

      {/* Email + phone rows for all contacts */}
      {contact.email ? (
        <a
          href={`mailto:${encodeURIComponent(contact.email)}`}
          data-testid={`contact-card-email-${contact.id}`}
          className="flex items-center gap-2 text-foreground text-xs leading-4 hover:text-[#1E1B17] min-w-0"
        >
          <EnvelopeIcon />
          <span className="grow truncate">{contact.email}</span>
        </a>
      ) : null}
      {contact.phone ? (
        <a
          href={`tel:${telHref(contact.phone)}`}
          data-testid={`contact-card-phone-${contact.id}`}
          className="flex items-center gap-2 text-foreground text-xs leading-4 hover:text-[#1E1B17] min-w-0"
        >
          <PhoneIcon />
          <span className="grow truncate">
            {formatPhone(contact.phone)}
          </span>
        </a>
      ) : null}
    </div>
  )
}

// Inline role editor — dotted underline on read, text
// input on edit. Saves on blur / Enter.
function RoleRow({
  contactId,
  role,
  onSetRole,
  submitting,
}: {
  contactId: number
  role: string | null
  onSetRole: (role: string) => void
  submitting: boolean
}) {
  const { t } = useLingui()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(role ?? "")

  if (editing) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false)
          // Submit whenever the value changed — including
          // clearing to empty, which the backend stores as nil.
          const next = draft.trim()
          if (next !== (role ?? "")) {
            onSetRole(next)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur()
          } else if (e.key === "Escape") {
            setDraft(role ?? "")
            setEditing(false)
          }
        }}
        data-testid={`contact-role-input-${contactId}`}
        className="text-xs leading-4 text-foreground bg-transparent border-b border-[#B89A6A] outline-none w-full"
        placeholder={t`Add role`}
        disabled={submitting}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(role ?? "")
        setEditing(true)
      }}
      data-testid={`contact-role-read-${contactId}`}
      className="text-xs leading-4 text-left cursor-pointer"
    >
      {role ? (
        <span className="text-[#7A746B] border-b border-dotted border-[#C9C2B6]">
          {role}
        </span>
      ) : (
        <span className="text-[#B9B0A4] border-b border-dotted border-[#C9C2B6]">
          <Trans>Add role</Trans>
        </span>
      )}
    </button>
  )
}

// ── Contact name (three modes) ──────────────────────────

function ContactName({
  contact,
  navContext,
  linkToContactShow,
}: {
  contact: DealDrawerRosterContact
  navContext?: DrawerNavContext
  linkToContactShow: boolean
}) {
  const className =
    "font-semibold text-foreground text-sm leading-[18px] truncate min-w-0"

  if (navContext) {
    return (
      <button
        type="button"
        onClick={() =>
          pushDrawer("contact", contact.id, navContext)
        }
        data-testid={`contact-card-name-${contact.id}`}
        className={`${className} text-left hover:underline cursor-pointer`}
      >
        {contact.displayName}
      </button>
    )
  }

  if (linkToContactShow) {
    return (
      <ContactNameLink
        contactId={contact.id}
        displayName={contact.displayName}
        className={className}
      />
    )
  }

  return <div className={className}>{contact.displayName}</div>
}

function ContactNameLink({
  contactId,
  displayName,
  className,
}: {
  contactId: number
  displayName: string
  className: string
}) {
  const { forward } = useReturnTo({ fallback: "/v2/deals" })
  return (
    <Link
      href={forward(`/v2/contacts/${contactId}`)}
      data-testid={`contact-card-name-${contactId}`}
      className={`${className} hover:underline cursor-pointer`}
    >
      {displayName}
    </Link>
  )
}

// ── Kebab menu ──────────────────────────────────────────

function KebabMenu({
  contact,
  open,
  onOpenChange,
  onMakePrimary,
  onRemove,
}: {
  contact: DealDrawerRosterContact
  open: boolean
  onOpenChange: (v: boolean) => void
  onMakePrimary: () => void
  onRemove: () => void
}) {
  const { t } = useLingui()

  // Non-primary contacts that are the only roster member:
  // can't remove (no primary to fall back to). Guard is on
  // the `primary` flag — the backend also enforces this.
  const canRemove = !contact.primary
  const canMakePrimary = !contact.primary

  if (!canRemove && !canMakePrimary) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        data-testid={`contact-kebab-${contact.id}`}
        aria-label={t`Contact options`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center justify-center rounded-full size-7 text-[#9A938A] hover:bg-[#F0EDE6] cursor-pointer"
      >
        <KebabIcon />
      </button>
      {open ? (
        <KebabPanel
          contactId={contact.id}
          canMakePrimary={canMakePrimary}
          canRemove={canRemove}
          onMakePrimary={onMakePrimary}
          onRemove={onRemove}
          onClose={() => onOpenChange(false)}
        />
      ) : null}
    </div>
  )
}

function KebabPanel({
  contactId,
  canMakePrimary,
  canRemove,
  onMakePrimary,
  onRemove,
  onClose,
}: {
  contactId: number
  canMakePrimary: boolean
  canRemove: boolean
  onMakePrimary: () => void
  onRemove: () => void
  onClose: () => void
}) {
  const { t: _t } = useLingui()

  // Escape closes the menu. Bound at the window level because
  // the scrim is non-focusable and aria-hidden, so it never
  // receives keydown events.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  // Click-outside dismiss
  const handleOutsideClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  return (
    <>
      {/* transparent scrim */}
      <div
        className="fixed inset-0 z-40"
        aria-hidden="true"
        onClick={handleOutsideClick}
      />
      <div
        role="menu"
        data-testid={`contact-kebab-panel-${contactId}`}
        className="absolute right-0 top-full mt-1 z-50 flex flex-col rounded-xl bg-card shadow-[0_8px_24px_rgba(30,27,23,0.14),0_2px_4px_rgba(30,27,23,0.06)] border border-[#E5DFD3] overflow-clip min-w-[164px]"
      >
        {canMakePrimary ? (
          <button
            type="button"
            role="menuitem"
            onClick={onMakePrimary}
            data-testid={`contact-make-primary-${contactId}`}
            className="flex items-center gap-2 py-2.5 px-3.5 text-foreground text-[13px] leading-4 hover:bg-[#FAF3EB] text-left cursor-pointer"
          >
            <StarIcon />
            <Trans>Make primary</Trans>
          </button>
        ) : null}
        {canRemove ? (
          <button
            type="button"
            role="menuitem"
            onClick={onRemove}
            data-testid={`contact-remove-${contactId}`}
            className="flex items-center gap-2 py-2.5 px-3.5 text-[#B84F4F] text-[13px] leading-4 hover:bg-[#FAF3EB] text-left cursor-pointer"
          >
            <RemoveIcon />
            <Trans>Remove from deal</Trans>
          </button>
        ) : null}
      </div>
    </>
  )
}

// ── Call affordance ─────────────────────────────────────

function CallButton({
  contactId,
  phone,
}: {
  contactId: number
  phone: string | null
}) {
  const { t } = useLingui()
  const { startCall, busy, disabled, disabledReason } = useStartCall({
    contactId,
    phone,
  })

  return (
    <button
      type="button"
      onClick={disabled || busy ? undefined : startCall}
      disabled={disabled || busy}
      data-testid={`contact-card-call-${contactId}`}
      aria-label={
        disabledReason ? t`Call (${disabledReason})` : t`Call`
      }
      title={disabledReason ?? undefined}
      className="shrink-0 flex items-center justify-center rounded-full size-8 bg-card border border-[#E5DFD3] text-foreground enabled:cursor-pointer enabled:hover:bg-[#FAF6EE] disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <CallGlyph />
    </button>
  )
}

// ── Utility helpers (exported for mobile card) ──────────

// Display formatter for phone numbers (US +1 grouping).
export function formatPhone(phone: string): string {
  const trimmed = phone.trim()
  if (!trimmed.startsWith("+1")) return trimmed
  const digits = trimmed.replace(/[^\d]/g, "")
  if (digits.length !== 11) return trimmed
  const area = digits.slice(1, 4)
  const exchange = digits.slice(4, 7)
  const subscriber = digits.slice(7, 11)
  return `+1 (${area}) ${exchange}-${subscriber}`
}

// Strip cosmetic formatting from a display number before
// handing it to the OS dialer (RFC 3966).
export function telHref(phone: string): string {
  return phone.replace(/[\s()\-.]/g, "")
}

// ── Icons ───────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="tracking-[0.08em] uppercase font-medium text-[#9A938A] text-[11px] leading-[14px]">
      {children}
    </div>
  )
}

function CallGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function EnvelopeIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <rect
        x="2"
        y="3.5"
        width="12"
        height="9"
        rx="1.5"
        stroke="#7A746B"
        strokeWidth="1.4"
      />
      <path
        d="M2.5 4.5L8 8.5L13.5 4.5"
        stroke="#7A746B"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M3 4C3 3.45 3.45 3 4 3H5.5C5.78 3 6.03 3.18 6.13 3.45L6.85 5.6C6.95 5.88 6.85 6.2 6.6 6.38L5.6 7.1C6.5 8.7 7.3 9.5 8.9 10.4L9.62 9.4C9.8 9.15 10.12 9.05 10.4 9.15L12.55 9.87C12.82 9.97 13 10.22 13 10.5V12C13 12.55 12.55 13 12 13C7 13 3 9 3 4Z"
        stroke="#7A746B"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function KebabIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="3.5" r="1.25" fill="#9A938A" />
      <circle cx="8" cy="8" r="1.25" fill="#9A938A" />
      <circle cx="8" cy="12.5" r="1.25" fill="#9A938A" />
    </svg>
  )
}

function StarIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M8 1.5L9.9 5.9L14.5 6.3L11.2 9.1L12.3 13.6L8 11L3.7 13.6L4.8 9.1L1.5 6.3L6.1 5.9L8 1.5Z"
        stroke="#9A938A"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function RemoveIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M3 8H13"
        stroke="#B84F4F"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}
